/**
 * Template Import/Export Component - Bulk template management for admin
 * 
 * Features:
 * - Export all templates to JSON file
 * - Import templates from JSON file with duplicate detection
 * - Skip or overwrite existing templates
 */

import React, { useState, useRef } from 'react';
import { Box, Button, Flex, Text, Card, Badge, Callout } from '@radix-ui/themes';
import { DownloadIcon, UploadIcon, InfoCircledIcon, CheckCircledIcon, Cross2Icon } from '@radix-ui/react-icons';
import { DeviceTemplate } from '../../types';
import { adminAPI, templatesAPI } from '../../services/api';

interface TemplateImportExportProps {
  adminKey: string;
  onUpdate: () => void;
}

const TemplateImportExport: React.FC<TemplateImportExportProps> = ({ adminKey, onUpdate }) => {
  const [importing, setImporting] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [importResult, setImportResult] = useState<{
    imported: number;
    skipped: number;
    errors: string[];
  } | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleExport = async () => {
    setExporting(true);
    try {
      const templates = await templatesAPI.getAll();
      
      // Create JSON blob
      const json = JSON.stringify(templates, null, 2);
      const blob = new Blob([json], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      
      // Trigger download
      const link = document.createElement('a');
      link.href = url;
      link.download = `astropower-templates-${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      
      console.log(`✅ Exported ${templates.length} templates`);
    } catch (error) {
      console.error('Export failed:', error);
      alert('Failed to export templates. Check console for details.');
    } finally {
      setExporting(false);
    }
  };

  const handleImport = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setImporting(true);
    setImportResult(null);

    try {
      const text = await file.text();
      const importedTemplates: DeviceTemplate[] = JSON.parse(text);

      if (!Array.isArray(importedTemplates)) {
        throw new Error('Invalid file format: expected array of templates');
      }

      // Get existing templates
      const existing = await templatesAPI.getAll();
      const existingByName = new Map(
        existing.map(t => [`${t.name.toLowerCase()}-${t.brand?.toLowerCase() || ''}-${t.model?.toLowerCase() || ''}`, t])
      );

      let imported = 0;
      let skipped = 0;
      const errors: string[] = [];

      for (const template of importedTemplates) {
        try {
          // Check for duplicate by name + brand + model
          const key = `${template.name.toLowerCase()}-${template.brand?.toLowerCase() || ''}-${template.model?.toLowerCase() || ''}`;
          const duplicate = existingByName.get(key);

          if (duplicate) {
            // Skip duplicates
            console.log(`Skipping duplicate: ${template.name} (${template.brand || 'no brand'})`);
            skipped++;
            continue;
          }

          // Import as pending for review (safer than auto-approving)
          await templatesAPI.submit({
            name: template.name,
            type: template.type,
            brand: template.brand,
            model: template.model,
            description: template.description,
            powerInput: template.powerInput,
            powerOutputs: template.powerOutputs,
            batteryCapacity: template.batteryCapacity,
            usbInputs: template.usbInputs,
            usbOutputs: template.usbOutputs,
          });

          // Immediately approve it since admin is importing
          const pending = await adminAPI.getPendingTemplates(adminKey);
          const justImported = pending.find(p => 
            p.name === template.name && 
            p.brand === template.brand && 
            p.model === template.model
          );
          
          if (justImported) {
            await adminAPI.approveTemplate(justImported.id, adminKey);
          }

          imported++;
        } catch (error) {
          console.error(`Failed to import ${template.name}:`, error);
          errors.push(`${template.name}: ${error instanceof Error ? error.message : 'Unknown error'}`);
        }
      }

      setImportResult({ imported, skipped, errors });
      
      if (imported > 0) {
        onUpdate(); // Refresh template list
      }
    } catch (error) {
      console.error('Import failed:', error);
      alert(`Failed to import templates: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setImporting(false);
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  return (
    <Box>
      <Card>
        <Flex direction="column" gap="4">
          <Box>
            <Text size="4" weight="bold" mb="2">
              📦 Bulk Template Management
            </Text>
            <Text size="2" color="gray">
              Export and import device templates in bulk
            </Text>
          </Box>

          <Callout.Root color="blue">
            <Callout.Icon>
              <InfoCircledIcon />
            </Callout.Icon>
            <Callout.Text>
              <strong>Import Strategy:</strong> Duplicates are automatically skipped based on name + brand + model.
              Imported templates are auto-approved since you're an admin.
            </Callout.Text>
          </Callout.Root>

          <Flex gap="3">
            <Button 
              size="3" 
              variant="solid" 
              onClick={handleExport}
              disabled={exporting}
            >
              <DownloadIcon /> {exporting ? 'Exporting...' : 'Export All Templates'}
            </Button>

            <Button 
              size="3" 
              variant="outline" 
              onClick={() => fileInputRef.current?.click()}
              disabled={importing}
            >
              <UploadIcon /> {importing ? 'Importing...' : 'Import Templates'}
            </Button>

            <input
              ref={fileInputRef}
              type="file"
              accept=".json"
              style={{ display: 'none' }}
              onChange={handleImport}
            />
          </Flex>

          {importResult && (
            <Card style={{ backgroundColor: 'var(--gray-2)' }}>
              <Flex direction="column" gap="2">
                <Text size="3" weight="bold" mb="1">
                  Import Results:
                </Text>
                
                {importResult.imported > 0 && (
                  <Flex align="center" gap="2">
                    <CheckCircledIcon color="green" />
                    <Text size="2">
                      <Badge color="green">{importResult.imported}</Badge> templates imported successfully
                    </Text>
                  </Flex>
                )}
                
                {importResult.skipped > 0 && (
                  <Flex align="center" gap="2">
                    <InfoCircledIcon color="orange" />
                    <Text size="2">
                      <Badge color="orange">{importResult.skipped}</Badge> duplicates skipped
                    </Text>
                  </Flex>
                )}
                
                {importResult.errors.length > 0 && (
                  <Box>
                    <Flex align="center" gap="2" mb="2">
                      <Cross2Icon color="red" />
                      <Text size="2" weight="bold">
                        <Badge color="red">{importResult.errors.length}</Badge> errors:
                      </Text>
                    </Flex>
                    <Box pl="4">
                      {importResult.errors.map((error, i) => (
                        <Text key={i} size="1" color="red" style={{ display: 'block' }}>
                          • {error}
                        </Text>
                      ))}
                    </Box>
                  </Box>
                )}
              </Flex>
            </Card>
          )}
        </Flex>
      </Card>
    </Box>
  );
};

export default TemplateImportExport;
